import { useEffect, useState } from "react";

export interface RiseCountOptions {
  /**
   * Counts the first known value from zero, so one above zero counts as a rise: the bell ringing
   * for the unread notifications it finds on load. Applies to the first value only, ever.
   */
  fromZero?: boolean;
}

interface RiseState {
  /** The last known value; undefined until one is known, and again while it is unknown. */
  last: number | undefined;
  rises: number;
  /** Whether any value has been known yet, so `fromZero` counts once. */
  seen: boolean;
  /** Known and above zero at mount under `fromZero`: counted after the first commit. */
  pending: boolean;
}

/**
 * How many times `value` has gone up since it was known: a value for `Icon`'s `playOn` that
 * changes when a count or a balance rises and never when it falls (a notification read, a spend).
 * `undefined` is "not known", never zero: the next known value is a fresh start, so data arriving
 * (or arriving again, as the shopper signs in) is not mistaken for a rise.
 */
export function useRiseCount(
  value: number | undefined,
  { fromZero = false }: RiseCountOptions = {},
): number {
  const [state, setState] = useState<RiseState>(() => ({
    last: value,
    rises: 0,
    seen: value !== undefined,
    pending: fromZero && value !== undefined && value > 0,
  }));
  // A rise must change the value after the first render for the icon to play, so a value already
  // known at mount is counted once the component has committed.
  useEffect(() => {
    if (state.pending) setState((s) => ({ ...s, rises: s.rises + 1, pending: false }));
  }, [state.pending]);
  if (value === state.last) return state.rises;
  // React's pattern for state that follows a prop: set it during render, not in an effect.
  const rose =
    value !== undefined &&
    (state.last === undefined ? fromZero && !state.seen && value > 0 : value > state.last);
  const rises = rose ? state.rises + 1 : state.rises;
  setState({
    last: value,
    rises,
    seen: state.seen || value !== undefined,
    pending: state.pending,
  });
  return rises;
}
